/**
 * VendorCatalogEditor — catalog manager for Return Gift / Flowers & Pooja vendors.
 *
 * These vendor categories sell discrete SKUs (per-piece / per-set / per-kg)
 * instead of guest-count packages. This editor lets them publish a catalog
 * of items (name, price, unit, image, tag) which then surfaces in the
 * Instant Pricing customer flow via /api/instant-pricing/return-gifts and
 * /api/instant-pricing/flowers-pooja.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, UploadSimple, Image as ImageIcon, FloppyDisk } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const newBlankItem = (fallbackTag) => ({
  id: `tmp_${Math.random().toString(36).slice(2, 10)}`,
  name: '',
  price: '',
  unit: '',
  image: '',
  category_tag: fallbackTag || '',
  description: '',
});

const fullUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND_URL}${p || ''}`);

const VendorCatalogEditor = ({ vendorCategory }) => {
  const [items, setItems] = useState([]);
  const [allowedTags, setAllowedTags] = useState([]);
  const [presets, setPresets] = useState([]);
  const [maxItems, setMaxItems] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputRefs = useRef({});

  const label = vendorCategory === 'return_gift'
    ? 'Return Gift Catalog'
    : 'Flowers & Pooja Catalog';
  const blurb = vendorCategory === 'return_gift'
    ? 'List up to 10 return gift items — Muhurtham Pai sets, Pooja Thalis, sweet boxes. Customers will see your shop card with these items as the menu in their Instant Pricing flow.'
    : 'List up to 10 items — garlands, bridal flowers, mandapam decoration, loose flowers, pooja items. Customers will see your shop card with these items as the menu. Available for all marriage types automatically.';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/catalog-items`);
        setItems(Array.isArray(data.items) ? data.items : []);
        setAllowedTags(Array.isArray(data.allowed_tags) ? data.allowed_tags : []);
        setPresets(Array.isArray(data.presets) ? data.presets : []);
        if (data.max_items) setMaxItems(data.max_items);
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to load catalog.';
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const remove = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const add = () => {
    if (items.length >= maxItems) {
      toast.error(`Maximum ${maxItems} items.`);
      return;
    }
    setItems((prev) => [...prev, newBlankItem(allowedTags[0])]);
  };

  /** Quick-add from preset — toggles the preset on/off and limits to maxItems. */
  const presetActive = (preset) => items.some((it) => (it.name || '').trim().toLowerCase() === preset.name.toLowerCase());

  const togglePreset = (preset) => {
    const isOn = presetActive(preset);
    if (isOn) {
      setItems((prev) => prev.filter((it) => (it.name || '').trim().toLowerCase() !== preset.name.toLowerCase()));
      return;
    }
    if (items.length >= maxItems) {
      toast.error(`You can list up to ${maxItems} items. Remove one first.`);
      return;
    }
    setItems((prev) => [...prev, {
      id: `tmp_${Math.random().toString(36).slice(2, 10)}`,
      name: preset.name,
      price: '',
      unit: preset.unit || '',
      image: '',
      category_tag: preset.category_tag || allowedTags[0] || '',
      description: '',
    }]);
  };

  const handleUpload = async (idx, file) => {
    if (!file) return;
    setUploadingFor(idx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/me/catalog-items/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update(idx, { image: data.url });
      toast.success('Image uploaded.');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Image upload failed.';
      toast.error(msg);
    } finally {
      setUploadingFor(null);
    }
  };

  const save = async () => {
    // Strip blank rows before saving — vendor convenience.
    const cleaned = items.filter((it) => (it.name || '').trim().length > 0);
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/me/catalog-items`, { items: cleaned });
      setItems(Array.isArray(data.items) ? data.items : []);
      toast.success(`Saved ${data.count} item${data.count === 1 ? '' : 's'}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-[#E8E1D5]" data-testid="vendor-catalog-editor-loading">
        <CardContent className="p-6 text-sm text-text-secondary">Loading catalog…</CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="vendor-catalog-editor">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-heading text-2xl">{label}</CardTitle>
            <CardDescription className="text-sm mt-1 max-w-2xl">{blurb}</CardDescription>
          </div>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
            data-testid="vendor-catalog-save-btn"
          >
            <FloppyDisk size={16} className="mr-2" />
            {saving ? 'Saving…' : 'Save Catalog'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick-add presets — tick from a curated list of common items.
            Tapping a preset toggles it in/out of the catalog with the
            preset's default category_tag and unit pre-filled (vendor still
            sets the price). Vendor can also use "Add Item" to type a custom
            name for items not in the list. */}
        {presets.length > 0 && (
          <div className="border border-[#E8E1D5] rounded p-3 bg-sand-50/40" data-testid="vendor-catalog-presets">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-text-secondary">Quick-add common items</div>
                <div className="text-[11px] text-text-secondary/80">
                  Tick what you sell. You can then set the price on each row below. Type a custom name in &quot;Add Item&quot; if your product isn&apos;t on this list.
                </div>
              </div>
              <div className="text-[11px] text-text-secondary font-bold whitespace-nowrap">
                {items.length}/{maxItems} listed
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((p) => {
                const active = presetActive(p);
                return (
                  <button
                    type="button"
                    key={p.name}
                    onClick={() => togglePreset(p)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border rounded-none transition-all ${active ? 'border-brand-primary bg-brand-accent/10 text-brand-primary font-bold ring-1 ring-brand-primary/30' : 'border-[#E8E1D5] bg-white hover:border-brand-primary/40 text-text-primary'}`}
                    data-testid={`vendor-catalog-preset-${p.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  >
                    <span className={`inline-block w-3 h-3 border rounded-sm flex items-center justify-center ${active ? 'bg-brand-primary border-brand-primary' : 'border-[#E8E1D5]'}`}>
                      {active && <span className="text-white text-[9px] leading-none font-bold">✓</span>}
                    </span>
                    {p.name}
                    <span className="text-[10px] text-text-secondary/70 ml-0.5">· {p.category_tag}</span>
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-text-secondary/70 mt-2 italic">
              Available for all wedding & event types automatically — no marriage-type selection needed.
            </div>
          </div>
        )}

        {items.length === 0 && (
          <div className="text-center py-6 text-sm text-text-secondary border border-dashed border-[#E8E1D5] rounded">
            No items yet. Tick presets above, or click <strong>+ Add Item</strong> below to publish your first SKU.
          </div>
        )}

        {items.map((it, idx) => (
          <div
            key={it.id || idx}
            className="border border-[#E8E1D5] rounded p-3 sm:p-4 grid grid-cols-12 gap-3"
            data-testid={`vendor-catalog-row-${idx}`}
          >
            {/* Image column */}
            <div className="col-span-12 sm:col-span-2 flex items-start justify-center">
              <div className="relative w-24 h-24 border border-[#E8E1D5] rounded overflow-hidden bg-sand-50/50 flex items-center justify-center">
                {it.image ? (
                  <img
                    src={fullUrl(it.image)}
                    alt={it.name || 'Catalog item'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={28} className="text-text-secondary/50" />
                )}
                <input
                  ref={(r) => { fileInputRefs.current[idx] = r; }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleUpload(idx, e.target.files?.[0])}
                  data-testid={`vendor-catalog-image-input-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[idx]?.click()}
                  className="absolute bottom-0 right-0 bg-white/90 hover:bg-white text-text-primary px-2 py-1 text-[10px] uppercase tracking-wider font-bold shadow"
                  data-testid={`vendor-catalog-upload-btn-${idx}`}
                >
                  <UploadSimple size={11} className="inline mr-1" />
                  {uploadingFor === idx ? '…' : 'Photo'}
                </button>
              </div>
            </div>

            {/* Fields */}
            <div className="col-span-12 sm:col-span-9 grid grid-cols-12 gap-2">
              <div className="col-span-12 sm:col-span-6">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">Item name</Label>
                <Input
                  type="text"
                  value={it.name || ''}
                  placeholder={vendorCategory === 'return_gift' ? 'e.g. Muhurtham Pai (Wedding)' : 'e.g. Jasmine Garland (10 ft)'}
                  onChange={(e) => update(idx, { name: e.target.value })}
                  className="rounded-none border-[#E8E1D5] h-10"
                  data-testid={`vendor-catalog-name-${idx}`}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">Price (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={it.price ?? ''}
                  onChange={(e) => update(idx, { price: e.target.value })}
                  placeholder="e.g. 300"
                  className="rounded-none border-[#E8E1D5] h-10"
                  data-testid={`vendor-catalog-price-${idx}`}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">Unit</Label>
                <Input
                  type="text"
                  value={it.unit || ''}
                  onChange={(e) => update(idx, { unit: e.target.value })}
                  placeholder="per piece / per kg"
                  className="rounded-none border-[#E8E1D5] h-10"
                  data-testid={`vendor-catalog-unit-${idx}`}
                />
              </div>
              <div className="col-span-12 sm:col-span-4">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">Category</Label>
                <select
                  value={it.category_tag || ''}
                  onChange={(e) => update(idx, { category_tag: e.target.value })}
                  className="w-full rounded-none border border-[#E8E1D5] h-10 px-2 bg-white text-sm"
                  data-testid={`vendor-catalog-tag-${idx}`}
                >
                  <option value="">— pick —</option>
                  {allowedTags.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-12 sm:col-span-8">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">Description (optional)</Label>
                <Input
                  type="text"
                  value={it.description || ''}
                  onChange={(e) => update(idx, { description: e.target.value })}
                  placeholder="Short note — material, size, includes…"
                  className="rounded-none border-[#E8E1D5] h-10"
                  data-testid={`vendor-catalog-desc-${idx}`}
                />
              </div>
            </div>

            {/* Remove button */}
            <div className="col-span-12 sm:col-span-1 flex items-start justify-end">
              <button
                type="button"
                onClick={() => remove(idx)}
                className="text-red-500 hover:text-red-700 p-2"
                title="Remove item"
                data-testid={`vendor-catalog-remove-${idx}`}
              >
                <Trash size={18} />
              </button>
            </div>
          </div>
        ))}

        <Button
          type="button"
          onClick={add}
          variant="outline"
          className="w-full rounded-none border-dashed border-[#E8E1D5] hover:bg-sand-50/60"
          data-testid="vendor-catalog-add-btn"
          disabled={items.length >= maxItems}
        >
          <Plus size={16} className="mr-2" />
          Add Item {items.length > 0 ? `(${items.length}/${maxItems})` : ''}
        </Button>
      </CardContent>
    </Card>
  );
};

export default VendorCatalogEditor;
